import { z } from 'zod';
import { inferenceHttp } from '@/lib/api/http-client';
import { ArtifactsService } from '@inference.engine/api-client/web';
import type {
  StrategicTerritoriesWidgetSchemaType,
  Territory,
} from '@inference.engine/widgets/web';
import { getSavedArtifactSnapshot } from '@/entities/artifact';
import { downloadArtifactPdfV2 } from '@/shared/api/pdf-download.api';
import { logger } from '@/lib/utils/logger';

// READ schema for the print page — deliberately looser than the GENERATION
// schema (`StrategicTerritoriesWidgetSchema`). It shape-checks only what the
// render indexes (a `title` and an `items` array) and tolerates historical
// artifacts: the generation `TerritorySchema` imposes exact cardinality
// (coreTensions/strategicBrief/validation lengths, pillars 3-4) that real saved
// territories predate, and the defensive render handles partial data.
const StrategicTerritoriesReadSchema = z
  .object({
    title: z.string().nullable(),
    items: z.array(z.looseObject({})),
  })
  .loose();

/** Registry `:kind` segment for the generic save-widget-as-artifact route. */
const STRATEGIC_TERRITORIES_SAVE_KIND = 'strategic-territories';

/** Saves the territory at `itemIndex` from the live widget artifact
 *  (`sourceArtifactId`) and returns the saved artifact's id. The backend
 *  resolves the territory content by id and keys the saved copy by the source
 *  widget's exchange + index, so re-saving the same territory is idempotent. */
export const saveStrategicTerritoriesArtifact = async (
  sourceArtifactId: string,
  { workspaceId, itemIndex }: { workspaceId: string; itemIndex: number },
): Promise<string> => {
  const response = await ArtifactsService.saveWidgetArtifact({
    client: inferenceHttp,
    path: { workspaceId, kind: STRATEGIC_TERRITORIES_SAVE_KIND },
    body: { sourceArtifactId, options: { itemIndex } },
  });

  if (!response.data) {
    throw new Error('Failed to save strategic territories artifact');
  }

  return response.data.id;
};

export const getSavedStrategicTerritoriesSnapshot = (
  workspaceId: string,
  artifactId: string,
): Promise<StrategicTerritoriesWidgetSchemaType> =>
  getSavedArtifactSnapshot<StrategicTerritoriesWidgetSchemaType>(workspaceId, artifactId);

/**
 * Fetch one territory from a strategic-territories artifact by `artifactId`.
 * A live widget artifact holds all territories (`itemIndex` selects one); a saved
 * artifact holds exactly one (index 0). Used by the `/download` print page.
 */
export const getSingleTerritory = async (
  artifactId: string,
  { workspaceId, itemIndex = 0 }: { workspaceId: string; itemIndex?: number },
): Promise<Territory> => {
  const raw = await getSavedArtifactSnapshot<unknown>(workspaceId, artifactId);
  // Shape-check at the fetch boundary so a genuinely malformed snapshot fails
  // here instead of crashing where `.items` is indexed for the render.
  // react-query surfaces a throw only as `isError`, so log the issues to keep
  // the failure reproducible.
  const parsed = StrategicTerritoriesReadSchema.safeParse(raw);
  if (!parsed.success) {
    logger.error('Malformed strategic-territories snapshot', {
      artifactId,
      issues: parsed.error.issues,
    });
    throw new Error(`Malformed strategic-territories snapshot ${artifactId}`);
  }
  const snapshot = raw as StrategicTerritoriesWidgetSchemaType;
  // Reject a non-integer/out-of-range index instead of silently falling back to
  // the first item: `?itemIndex=abc` reaches here as NaN, and `.at(NaN)` coerces
  // to 0, so an unguarded lookup would 200 with the wrong territory.
  if (!Number.isInteger(itemIndex) || itemIndex < 0 || itemIndex >= snapshot.items.length) {
    throw new Error(`No territory at index ${itemIndex} in artifact ${artifactId}`);
  }
  return snapshot.items[itemIndex];
};

/** Download a single territory as a server-rendered PDF blob (see docs → PDF Export). */
export const downloadTerritoryPdf = (
  artifactId: string,
  { workspaceId, itemIndex }: { workspaceId: string; itemIndex: number },
): Promise<Blob> =>
  downloadArtifactPdfV2(artifactId, {
    // eslint-disable-next-line i18next/no-literal-string -- interpolated into the diagnostic `pdf-download.api.ts` throws; the caller toasts its own translated message
    label: 'Strategic territory',
    documentType: 'strategic-territory',
    workspaceId,
    itemIndex,
  });
